import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { X, Save, Trash2, ArrowUp, ArrowDown, Image as ImageIcon, Film } from "lucide-react";
import { ImageUploadField } from "@/pages/admin/fields/ImageUploadField";
import { PortfolioVideoUploadField } from "@/pages/admin/fields/PortfolioVideoUploadField";
import { Section } from "@/pages/admin/fields/primitives";
import { PortfolioCategoryPicker } from "@/pages/admin/fields/PortfolioCategoryPicker";
import { PortfolioVideoCoverField } from "@/pages/admin/fields/PortfolioVideoCoverField";

// Portfolio CMS — Auto-Detect Video Orientation (Feb 2026) +
// Auto-Detect Image Orientation (shape-based pairing, Feb 2026).
// Reads intrinsic (videoWidth × videoHeight) or (naturalWidth × naturalHeight)
// via a hidden HTML element. Uses the SAME 1.3 / 0.85 thresholds as the
// public gallery classifier so the whole system speaks one vocabulary —
// grouping compares orientation only, so an image + video with matching
// orientation always pair regardless of media type. Resolves null if
// the browser can't decode metadata — the caller then falls back to the
// stored/default orientation.
const classifyRatio = (w, h) => {
  if (!w || !h) return null;
  const r = w / h;
  if (r >= 1.3) return "landscape";
  if (r < 0.85) return "portrait";
  return "square";
};
const detectVideoOrientation = (url) => new Promise((resolve) => {
  if (!url) return resolve(null);
  try {
    const v = document.createElement("video");
    v.preload = "metadata";
    v.muted = true;
    v.playsInline = true;
    let done = false;
    const finish = (o) => { if (done) return; done = true; resolve(o); };
    v.onloadedmetadata = () => finish(classifyRatio(v.videoWidth, v.videoHeight));
    v.onerror = () => finish(null);
    v.src = url;
    setTimeout(() => finish(null), 8000);
  } catch { resolve(null); }
});
const detectImageOrientation = (url) => new Promise((resolve) => {
  if (!url) return resolve(null);
  try {
    const im = new Image();
    let done = false;
    const finish = (o) => { if (done) return; done = true; resolve(o); };
    im.onload = () => finish(classifyRatio(im.naturalWidth, im.naturalHeight));
    im.onerror = () => finish(null);
    im.src = url;
    setTimeout(() => finish(null), 8000);
  } catch { resolve(null); }
});
const detectOrientation = (asset) => (
  asset?.kind === "video"
    ? detectVideoOrientation(asset.url)
    : detectImageOrientation(asset?.url)
);

/**
 * Portfolio project editor — Mixed-Media V1 (Feb 2026).
 *
 *   MINIMAL WRITING. MAXIMUM VISUAL IMPACT.
 *
 * The old Quick Add / Full Case Study dual-mode was more workflow than
 * Portfolio actually needs. Now: ONE simple flow — Title, Client,
 * Category, Description, Cover, mixed-media gallery, Featured, Draft /
 * Published. Case-study narrative sections + services_used are legacy
 * fields — the underlying data is preserved on save so historic records
 * stay intact, but neither is authored or rendered publicly any more.
 *
 * Media items support:
 *   • Add Image  → single image asset (orientation not editable).
 *   • Add Video  → MP4/WebM up to 25 MB via /admin/upload-portfolio-video.
 *                  Admin picks orientation (Landscape 16:9 / Portrait 9:16
 *                  / Square 1:1) and optionally a poster image.
 *
 * The `gallery` array is the source of truth. The legacy `gallery_images`
 * array is preserved on the record (so read-fallback on the detail page
 * keeps working for pre-Mixed-Media projects) but no new content flows
 * into it. New projects author only `gallery[]`.
 */
export const PortfolioEditorDrawer = ({ item, onClose, onSave, services: _services = [] }) => {
  const isExisting = !!item?.id;

  // Legacy gallery_images → seed gallery[] for the editor UI so admins
  // can reorder / remove them consistently with new video assets. On save
  // we write the resulting gallery[] back and clear gallery_images (the
  // public reader still falls back to gallery_images for records that
  // never touch the editor).
  const initialGallery = useMemo(() => {
    if (Array.isArray(item?.gallery) && item.gallery.length > 0) {
      return item.gallery.map((a) => ({
        kind: a.kind === "video" ? "video" : "image",
        url: a.url || "",
        poster: a.poster || "",
        orientation: a.orientation || "landscape",
        alt: a.alt || "",
      }));
    }
    const legacy = Array.isArray(item?.gallery_images) ? item.gallery_images : [];
    return legacy.filter(Boolean).map((url) => ({
      kind: "image",
      url,
      poster: "",
      orientation: "landscape",
      alt: "",
    }));
  }, [item]);

  const [f, setF] = useState({
    id: item?.id,
    slug: item?.slug || "",
    title: item?.title || "",
    client: item?.client || "",
    description: item?.description || "",
    category: item?.category || "",
    image: item?.image || "",
    homepage_video_cover: item?.homepage_video_cover || "",
    homepage_video_cover_meta: item?.homepage_video_cover_meta || null,
    gallery: initialGallery,
    // Preserved but never surfaced in this editor. Passed through on save
    // so historic case-study data + service tags survive edits.
    gallery_images: Array.isArray(item?.gallery_images) ? item.gallery_images : [],
    case_study_sections: Array.isArray(item?.case_study_sections) ? item.case_study_sections : [],
    services_used: Array.isArray(item?.services_used) ? item.services_used : [],
    categories: Array.isArray(item?.categories) ? item.categories : [],
    primary_category: item?.primary_category || "",
    featured: !!item?.featured,
    status: item?.status || (isExisting ? "published" : "draft"),
    display_order: item?.display_order || 0,
  });
  const [saving, setSaving] = useState(false);
  // Save-keeps-editor-open (Feb 2026): a light-weight timestamp used
  // to render an in-header "Saved" pill for ~2s after a successful
  // Save. Toasts confirm the operation, but Calvin also wants a
  // visible confirmation IN the drawer so it's obvious the change
  // persisted and the drawer intentionally stayed open.
  const [justSavedAt, setJustSavedAt] = useState(0);
  const savedRecently = justSavedAt > 0 && Date.now() - justSavedAt < 2400;
  useEffect(() => {
    if (!justSavedAt) return;
    const t = setTimeout(() => setJustSavedAt(0), 2400);
    return () => clearTimeout(t);
  }, [justSavedAt]);

  const update = (patch) => setF((prev) => ({ ...prev, ...patch }));

  const slugify = (s) => (s || "")
    .toString()
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  const submit = async () => {
    if (!f.title) { toast.error("Title is required"); return; }
    if (!f.image) { toast.error("Cover image is required"); return; }
    setSaving(true);
    try {
      // Portfolio Mixed Portrait Pair Fix (Feb 2026) — Save-race guard.
      // `addImageAsset` / `addVideoAsset` append with a landscape default
      // and kick off async detection. If the operator hits Save before
      // that detection resolves (very possible for videos loading
      // metadata over the network), the payload previously serialized
      // the stale default and persisted "landscape" for a portrait
      // asset — invisible in the CMS on reload (mount-time detection
      // re-runs and shows the correct label), but the public gallery
      // reads the persisted value and pairing failed.
      //
      // Structural fix: await the SAME detectOrientation() the drawer is
      // already running, for every asset that isn't yet detected, and
      // fold the results into the payload. No timeouts, no delays —
      // just correctness gating on the existing dependency.
      const currentGallery = f.gallery || [];
      const undetected = currentGallery
        .map((a, i) => ({ a, i }))
        .filter(({ a }) => a?.url && !a._orientationDetected);
      let resolvedGallery = currentGallery;
      if (undetected.length > 0) {
        const results = await Promise.all(
          undetected.map(({ a }) =>
            detectOrientation(a).then((orientation) => ({
              url: a.url,
              kind: a.kind,
              orientation,
            }))
          )
        );
        resolvedGallery = [...currentGallery];
        results.forEach(({ url, kind, orientation }) => {
          for (let k = 0; k < resolvedGallery.length; k += 1) {
            const g = resolvedGallery[k];
            if (g?.url === url && g?.kind === kind && !g._orientationDetected) {
              resolvedGallery[k] = {
                ...g,
                orientation: orientation || g.orientation || "landscape",
                _orientationDetected: true,
              };
              break;
            }
          }
        });
        // Reflect newly-detected orientations in local state so the
        // labels update after save and future submits skip re-detection.
        setF((prev) => ({ ...prev, gallery: resolvedGallery }));
      }
      const cleanGallery = resolvedGallery
        .filter((a) => a && a.url)
        .map((a) => ({
          kind: a.kind === "video" ? "video" : "image",
          url: a.url,
          poster: a.poster || null,
          orientation: a.orientation || "landscape",
          alt: a.alt || "",
          // NOTE: `_orientationDetected` (internal editor flag) is intentionally
          // omitted so it never ends up in the persisted MediaAsset record.
        }));
      const payload = {
        ...f,
        slug: f.slug || slugify(f.title),
        gallery: cleanGallery,
      };
      // Save-keeps-editor-open (Feb 2026): request keepOpen so the
      // parent skips its close side-effect and returns the persisted
      // document. Sync id / slug / display_order back into local
      // state so the very next Save on this same drawer is an UPDATE
      // instead of a duplicate CREATE, and reflect any server-side
      // normalization (slug canonicalization, auto display_order,
      // cleaned gallery) back into the form.
      const saved = await onSave(payload, { keepOpen: true });
      if (saved) {
        setF((prev) => ({
          ...prev,
          id: saved.id || prev.id,
          slug: saved.slug || prev.slug,
          display_order: typeof saved.display_order === "number" ? saved.display_order : prev.display_order,
        }));
      }
      setJustSavedAt(Date.now());
    } catch {
      // Parent surfaces its own error toast; keep the editor open on failure
      // so the admin doesn't lose in-flight edits.
    } finally {
      setSaving(false);
    }
  };

  const atCap = (f.gallery || []).length >= 10;

  const addImageAsset = (url) => {
    if (!url || atCap) return;
    // Same async detect-and-swap pattern as video (below) so both media
    // types acquire an accurate orientation before any pairing decision.
    setF((prev) => ({
      ...prev,
      gallery: [
        ...(prev.gallery || []),
        { kind: "image", url, poster: "", orientation: "landscape", alt: "" },
      ],
    }));
    detectImageOrientation(url).then((detected) => {
      if (!detected) return;
      setF((prev) => {
        const g = [...(prev.gallery || [])];
        for (let k = g.length - 1; k >= 0; k -= 1) {
          if (g[k]?.kind === "image" && g[k].url === url && !g[k]._orientationDetected) {
            g[k] = { ...g[k], orientation: detected, _orientationDetected: true };
            break;
          }
        }
        return { ...prev, gallery: g };
      });
    });
  };
  const addVideoAsset = (url) => {
    if (!url || atCap) return;
    // Append immediately with a safe landscape default so the UI stays
    // responsive, then swap to the detected orientation as soon as the
    // browser resolves the video's intrinsic dimensions. Detection
    // failure keeps the landscape fallback rather than blocking add.
    setF((prev) => ({
      ...prev,
      gallery: [
        ...(prev.gallery || []),
        { kind: "video", url, poster: "", orientation: "landscape", alt: "" },
      ],
    }));
    detectVideoOrientation(url).then((detected) => {
      if (!detected) return;
      setF((prev) => {
        const g = [...(prev.gallery || [])];
        // Match by url — index may have shifted if the admin reordered mid-detect.
        for (let k = g.length - 1; k >= 0; k -= 1) {
          if (g[k]?.kind === "video" && g[k].url === url && !g[k]._orientationDetected) {
            g[k] = { ...g[k], orientation: detected, _orientationDetected: true };
            break;
          }
        }
        return { ...prev, gallery: g };
      });
    });
  };

  // Existing-project media also flow through auto-detect on load — one
  // pass covers BOTH images and videos so pairing is fully shape-based.
  // Detection success updates the stored orientation (persisted on next
  // Save); detection failure preserves the stored value so legacy
  // records never get clobbered.
  useEffect(() => {
    const gallery = f.gallery || [];
    const targets = gallery
      .map((a, i) => ({ a, i }))
      .filter(({ a }) => a?.url && !a._orientationDetected);
    if (targets.length === 0) return;
    let cancelled = false;
    Promise.all(
      targets.map(({ a, i }) => detectOrientation(a).then((o) => ({ i, url: a.url, kind: a.kind, orientation: o })))
    ).then((results) => {
      if (cancelled) return;
      setF((prev) => {
        const g = [...(prev.gallery || [])];
        results.forEach(({ url, kind, orientation }) => {
          // Match by url + kind so reordering during detection is safe.
          for (let k = 0; k < g.length; k += 1) {
            if (g[k]?.url === url && g[k]?.kind === kind && !g[k]._orientationDetected) {
              g[k] = {
                ...g[k],
                orientation: orientation || g[k].orientation || "landscape",
                _orientationDetected: true,
              };
              break;
            }
          }
        });
        return { ...prev, gallery: g };
      });
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const updateAsset = (i, patch) => {
    update({ gallery: f.gallery.map((a, idx) => (idx === i ? { ...a, ...patch } : a)) });
  };
  const removeAsset = (i) => {
    update({ gallery: f.gallery.filter((_, idx) => idx !== i) });
  };
  const moveAsset = (i, dir) => {
    const arr = [...f.gallery];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    update({ gallery: arr });
  };

  const [pendingVideoUrl, setPendingVideoUrl] = useState("");

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="portfolio-editor">
      <div className="flex-1 bg-black/60" onClick={onClose} />
      <div className="w-full max-w-2xl bg-[#0F0F0F] border-l border-[#262626] overflow-y-auto">
        <div className="sticky top-0 z-10 bg-[#0F0F0F] border-b border-[#262626] px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#FF6600] font-bold">
              Portfolio Project
              {f.status === "draft" && <span className="ml-1 text-[#F59E0B]">· Draft</span>}
            </div>
            <div className="font-display text-xl font-bold text-white mt-0.5 truncate">
              {f.title || "New Project"}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {savedRecently && (
              <span
                data-testid="portfolio-saved-indicator"
                className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 text-emerald-400 text-[11px] font-bold tracking-[0.15em] uppercase px-2.5 py-1"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Saved
              </span>
            )}
            <button
              type="button"
              data-testid="portfolio-save"
              onClick={submit}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-full bg-[#FF6600] text-white font-semibold text-sm px-4 py-2 hover:bg-[#E65C00] disabled:opacity-50"
            >
              <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              data-testid="portfolio-close"
              onClick={onClose}
              className="w-9 h-9 grid place-items-center rounded-full hover:bg-white/5"
            >
              <X className="w-4 h-4 text-white" />
            </button>
          </div>
        </div>

        {/* Status strip */}
        <div className="border-b border-[#262626] bg-[#0F0F0F] px-6 py-3 flex items-center justify-between gap-4">
          <div className="text-[11px] leading-snug">
            <span className="text-[#A3A3A3]">Status:</span>{" "}
            {f.status === "draft" ? (
              <span className="text-[#F59E0B] font-bold">Draft — hidden from public site</span>
            ) : (
              <span className="text-emerald-400 font-bold">Published — visible to visitors</span>
            )}
          </div>
          <div className="flex items-center gap-1 rounded-full border border-[#262626] bg-[#0B0B0B] p-1" role="group">
            <button
              type="button"
              data-testid="portfolio-status-draft"
              onClick={() => update({ status: "draft" })}
              className={`px-3 py-1 rounded-full text-[11px] font-bold tracking-[0.15em] uppercase transition-colors ${
                f.status === "draft" ? "bg-[#F59E0B] text-black" : "text-white/70 hover:text-white"
              }`}
            >
              Draft
            </button>
            <button
              type="button"
              data-testid="portfolio-status-published"
              onClick={() => update({ status: "published" })}
              className={`px-3 py-1 rounded-full text-[11px] font-bold tracking-[0.15em] uppercase transition-colors ${
                f.status === "published" ? "bg-emerald-500 text-black" : "text-white/70 hover:text-white"
              }`}
            >
              Published
            </button>
          </div>
        </div>

        <div className="p-6 space-y-8" data-testid="portfolio-form">
          <Section variant="subtle" title="Project Basics" destination="Renders on: Portfolio grid + /portfolio/[slug] detail page hero">
            <div className="grid grid-cols-2 gap-3">
              <TextField label="Project Name" value={f.title} onChange={(v) => update({ title: v })} required testId="portfolio-title" />
              <TextField label="Client" value={f.client} onChange={(v) => update({ client: v })} testId="portfolio-client" />
              <TextField label="Category / Tags" value={f.category} onChange={(v) => update({ category: v })} placeholder="e.g. Branding & 3D Visualization" testId="portfolio-category" />
              <TextField label="Sort order" type="number" value={String(f.display_order)} onChange={(v) => update({ display_order: Number(v) || 0 })} compact />
              <div className="col-span-2">
                <PortfolioCategoryPicker
                  categories={f.categories}
                  primary={f.primary_category}
                  onChange={(categories, primary_category) => update({ categories, primary_category })}
                />
              </div>
              <div className="col-span-2">
                <TextField
                  label="Short Description (optional)"
                  value={f.description}
                  onChange={(v) => update({ description: v })}
                  placeholder="One line under the hero — keep it short. Optional."
                  testId="portfolio-description"
                />
              </div>
              <div className="col-span-2 flex items-center gap-4">
                <label className="inline-flex items-center gap-2 text-sm text-white">
                  <input
                    type="checkbox"
                    data-testid="portfolio-featured"
                    checked={f.featured}
                    onChange={(e) => update({ featured: e.target.checked })}
                    className="w-4 h-4 accent-[#FF6600]"
                  />
                  Featured on homepage
                </label>
                <span className="text-[11px] italic text-[#FF6600]/70">→ appears in Homepage → Featured Projects deck</span>
              </div>
            </div>
          </Section>

          <Section variant="subtle" title="Cover Image" destination="Renders on: Homepage → Featured Projects deck AND /portfolio grid thumbnail" hint="Always required. Shown as the still image on cards, and as the fallback whenever a project video is not playing.">
            <ImageUploadField
              value={f.image}
              onChange={(url) => update({ image: url })}
              testId="portfolio-cover-upload"
            />
          </Section>

          <Section
            variant="subtle"
            title="Project Video (optional)"
            destination="Renders on: Homepage → Featured Projects card AND /portfolio/[slug] video player"
            hint="Upload once — it is used in both places. For published + featured projects the homepage card plays it muted and looping whenever the card is on screen. The project page autoplays the full video muted, with controls for pause and sound. Any shape works (16:9, 1:1, 9:16, 4:5). MP4 (H.264) recommended for iPhone."
          >
            <PortfolioVideoCoverField
              value={f.homepage_video_cover}
              meta={f.homepage_video_cover_meta}
              onChange={(url, meta) => update({ homepage_video_cover: url, homepage_video_cover_meta: meta })}
            />
          </Section>

          <Section
            variant="subtle"
            title="Project Media"
            destination="Renders on: /portfolio/[slug] detail page — mixed-media gallery"
            hint="Add images and videos in the order you want them shown. Video plays inside the project (not on the homepage). Reorder freely."
          >
            <div className="space-y-3" data-testid="portfolio-gallery-list">
              {f.gallery.map((a, i) => (
                <div key={`${a.url}-${i}`} data-testid={`portfolio-asset-${i}`} className="p-3 rounded-xl bg-[#141414] border border-[#262626] space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-14 h-14 rounded-lg bg-[#0F0F0F] grid place-items-center shrink-0 overflow-hidden">
                      {a.kind === "video" ? (
                        <Film className="w-5 h-5 text-[#FF6600]" />
                      ) : (
                        <img src={a.url} alt="" className="w-full h-full object-cover" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] uppercase tracking-widest text-[#FF6600] font-bold flex items-center gap-1.5">
                        {a.kind === "video" ? (<><Film className="w-3 h-3" /> Video</>) : (<><ImageIcon className="w-3 h-3" /> Image</>)}
                        {/* Shape-Based Media Structure (Feb 2026) — show the
                            auto-detected orientation for BOTH images and
                            videos so Calvin can see how a given asset will
                            pair on the public page. Informational only. */}
                        {a._orientationDetected && (
                          <span data-testid={`portfolio-asset-${i}-orientation-label`} className="text-[#A3A3A3] font-normal">· {a.orientation}</span>
                        )}
                      </div>
                      <div className="text-xs text-[#A3A3A3] truncate mt-0.5">{a.url}</div>
                    </div>
                    <button type="button" onClick={() => moveAsset(i, -1)} disabled={i === 0} data-testid={`portfolio-asset-${i}-up`} className="w-8 h-8 grid place-items-center rounded-md hover:bg-white/5 disabled:opacity-30">
                      <ArrowUp className="w-4 h-4" />
                    </button>
                    <button type="button" onClick={() => moveAsset(i, 1)} disabled={i === f.gallery.length - 1} data-testid={`portfolio-asset-${i}-down`} className="w-8 h-8 grid place-items-center rounded-md hover:bg-white/5 disabled:opacity-30">
                      <ArrowDown className="w-4 h-4" />
                    </button>
                    <button type="button" onClick={() => removeAsset(i)} data-testid={`portfolio-asset-${i}-remove`} className="w-8 h-8 grid place-items-center rounded-md text-[#FF6600] hover:bg-[#FF6600]/10">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  {a.kind === "video" && (
                    <div className="pl-1">
                      <label className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">Poster (optional)</label>
                      <ImageUploadField
                        value={a.poster || ""}
                        onChange={(url) => updateAsset(i, { poster: url })}
                        testId={`portfolio-asset-${i}-poster-upload`}
                      />
                    </div>
                  )}
                </div>
              ))}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                {atCap ? (
                  <div
                    data-testid="portfolio-media-cap-reached"
                    className="sm:col-span-2 rounded-xl border border-dashed border-[#333] bg-[#141414] px-4 py-6 text-center"
                  >
                    <div className="text-[10px] uppercase tracking-widest text-[#FF6600] font-bold">
                      Maximum media reached
                    </div>
                    <div className="mt-1 text-[12px] text-[#A3A3A3]">
                      10 / 10 assets. Remove one above to add another.
                    </div>
                  </div>
                ) : (
                  <>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] mb-1">
                        Add Image <span className="text-[#7A7A7A] normal-case tracking-normal">({f.gallery.length}/10)</span>
                      </div>
                      <ImageUploadField
                        value=""
                        onChange={addImageAsset}
                        testId="portfolio-add-image"
                      />
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] mb-1">
                        Add Video <span className="text-[#7A7A7A] normal-case tracking-normal">({f.gallery.length}/10)</span>
                      </div>
                      <PortfolioVideoUploadField
                        value={pendingVideoUrl}
                        onChange={(url) => {
                          if (url) {
                            addVideoAsset(url);
                            setPendingVideoUrl("");
                          } else {
                            setPendingVideoUrl("");
                          }
                        }}
                        testId="portfolio-add-video"
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
};

const TextField = ({ label, value, onChange, placeholder = "", required, type = "text", compact, testId }) => (
  <div>
    <label className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">
      {label} {required && <span className="text-[#FF6600]">*</span>}
    </label>
    <input
      data-testid={testId}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`mt-1 w-full bg-[#141414] text-white border border-[#262626] rounded-md ${compact ? "px-2 py-1.5" : "px-3 py-2"} text-sm placeholder:text-[#666] focus:border-[#FF6600] outline-none`}
    />
  </div>
);
